import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Paper,
  Stack,
  Chip,
  Button,
  Checkbox,
  IconButton,
  Divider,
  Collapse,
  CircularProgress,
} from "@mui/material";
import { useTranslate } from "src/locales";
import { useSnackbar } from "notistack";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { ByzMuiDatePicker } from "src/components/core-components/mui/inputs/ByzMuiDatePicker";
import ByzMuiButton from "src/components/core-components/mui/buttons/ByzMuiButton";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { surgeryService } from "src/api/surgery/surgeryService";
import PatientDatas from "src/modules/medcard/patient-datas";
import { useSelector } from "react-redux";
import { RootState } from "src/app/store";
import { HospitalBuildingResponseDto } from "src/api/references/referenceServicesDtos";
import FloorList from "src/components/shared-components/floor-list";
import DepartmentList from "src/components/shared-components/department-list";
import BuildingFilter from "src/components/core-components/ByzFilterCard/filters/BuildingFilter";
import { useAppSelector } from "src/app/hooks";
import ByzNursePrintButton2 from "src/components/core-components/mui/buttons/ByzNurseButton/ByzNursePrintButton2";

interface SurgeryData {
  orderid: number;
  id: number;
  ptid: number;
  ptid2: number;
  upn: number;
  deptid: number;
  otime: string;
  diseasecodes: string;
  iproperations: string;
  psname2: string;
  patinfo: string;
  patientname: string;
  patientsurname: string;
  sexuality: string;
  deptname: string;
  ptdrname: string;
  iscomplatepreop: number;
  serdistime: string;
  acceptforop: string;
  psid2: number;
}

interface ApiResponse {
  data: SurgeryData[];
  issuccess: boolean;
  statuscode: number;
  message: string;
}

interface SurgeryListProps {
  patientId?: number | null;
}

export default function SurgeryList({
  patientId: propPatientId,
}: SurgeryListProps) {
  const { t } = useTranslate();
  const { enqueueSnackbar } = useSnackbar();
  //const { id } = useParams();
  const selectedOfficeId = useSelector(
    (state: RootState) => state.auth.selectedOfficeId
  );
  const { user } = useAppSelector((state) => state.auth);
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [building, setBuilding] = useState<HospitalBuildingResponseDto | null>(
    null
  );
  const [floor, setFloor] = useState<any>(null);
  const [department, setDepartment] = useState<any>(null);
  const [surgeryData, setSurgeryData] = useState<SurgeryData[]>([]);
  const [expandedCards, setExpandedCards] = useState<number[]>([]);
  const [closingCards, setClosingCards] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [preopLoading, setPreopLoading] = useState<number | null>(null);

  const mainTabs = useSelector((state: any) => state.tabs.mainTabs);
  const activeTabIndex = useSelector((state: any) => state.tabs.activeTabIndex);
  const activeTab = mainTabs[activeTabIndex];

  // Hasta bakım planı verisini çek
  const tabPatientId = activeTab?.data?.patientId;

  // Component mount olduğunda veya patientId değiştiğinde çalışır
  const currentPatientId: number = propPatientId || tabPatientId || 0;

  // Tarih değiştirme fonksiyonları
  const handlePreviousDay = () => {
    if (selectedDate) {
      const previousDay = new Date(selectedDate);
      previousDay.setDate(previousDay.getDate() - 1);
      setSelectedDate(previousDay);
    }
  };

  const handleNextDay = () => {
    if (selectedDate) {
      const nextDay = new Date(selectedDate);
      nextDay.setDate(nextDay.getDate() + 1);
      setSelectedDate(nextDay);
    }
  };

  // Bina seçildiğinde kat seçimini sıfırla
  const handleBuildingChange = useCallback((value: HospitalBuildingResponseDto | null) => {
    setBuilding(value);
    setFloor(null); // Kat seçimini sıfırla
  }, []);

  // Floor değişikliği için callback
  const handleFloorChange = useCallback((value: any) => {
    // Null kontrolü yapıyoruz
    if (value === null || value === undefined) {
      setFloor(null);
    } else {
      setFloor(value);
    }
  }, []);

  // Department değişikliği için callback
  const handleDepartmentChange = useCallback((value: any) => {
    setDepartment(value);
  }, []);

  // Reset building when office changes
  useEffect(() => {
    if (selectedOfficeId && user) {
      setBuilding(null);
      setFloor(null);
    }
  }, [selectedOfficeId, user]);

  const loadData = useCallback(async () => {
    try {
      setIsLoading(true);
      setSurgeryData([]);

      const requestParams: any = { ptid: currentPatientId };

      if (currentPatientId === 0 && selectedDate) {
        const formattedDate = selectedDate.toISOString().split("T")[0];
        requestParams.startdate = formattedDate;
      }

      if (currentPatientId === 0) {
        if (building?.id) requestParams.buildingId = building.id;
        if (floor && (floor.id || floor.ID)) requestParams.floorId = floor.id || floor.ID;
        if (department && (department.id || department.ID))
          requestParams.departmentId = department.id || department.ID;
      }

      const response: ApiResponse =
        await surgeryService.inpatientRequest.getInpatientRequestDetails(
          requestParams
        );

      if (response.issuccess && response.data) {
        setSurgeryData(response.data);
      } else {
        console.error("API response not successful:", response.message);
      }
    } catch (error) {
      console.error("GetInpatientRequestDetails Error:", error);
    } finally {
      setIsLoading(false);
    }
  }, [currentPatientId, selectedDate, building, floor, department]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleToggleExpand = (cardId: number) => {
    if (expandedCards.includes(cardId)) {
      // Kapanma animasyonu
      setClosingCards((prev) => [...prev, cardId]);
      setTimeout(() => {
        setExpandedCards((prev) => prev.filter((id) => id !== cardId));
        setClosingCards((prev) => prev.filter((id) => id !== cardId));
      }, 100);
    } else {
      // Açılma
      setExpandedCards((prev) => [...prev, cardId]);
    }
  };

  // Handle "Preop Hazırlıkları Tamam" button click
  const handlePreopComplete = async (
    surgery: SurgeryData,
    statusId: number
  ) => {
    if (!surgery) {
      enqueueSnackbar(
        t("nurse-surgery-procedure.messages.patientNotSelected"),
        {
          variant: "warning",
          autoHideDuration: 3000,
        }
      );
      return;
    }
    // Check current status first

    const checkStatus =
      await surgeryService.inpatientRequest.getCheckInpatientRequestStatus(
        surgery.id
      );
    console.log("🔍 Check Status:", checkStatus);
    console.log("🔍 Current Surgery Status:", surgery.psid2);

    if (checkStatus.data !== surgery.psid2) {
      enqueueSnackbar(t("nurse-surgery-procedure.messages.statusOutdated"), {
        variant: "info",
        autoHideDuration: 3000,
      });
      // Refresh data
      loadData();
      return;
    }

    // Set loading state for this specific surgery
    setPreopLoading(surgery.id);

    try {
      const processData = {
        IrId: surgery.id,
        StateId: statusId, // Preop Hazırlıkları Tamam status
        Ptime: new Date().toISOString(),
        IctId: null,
        IctNote: null,
      };

      await surgeryService.inpatientRequest.postCreateInpatientRequestProcess(
        processData
      );
      if (statusId === 1655) {
        enqueueSnackbar(t("nurse-surgery-procedure.messages.preopCompleted"), {
          variant: "success",
          autoHideDuration: 3000,
        });
      } else if (statusId === 1660) {
        enqueueSnackbar(
          t("nurse-surgery-procedure.messages.sentToOperatingRoom"),
          {
            variant: "success",
            autoHideDuration: 3000,
          }
        );
      } else if (statusId === 1715) {
        enqueueSnackbar(
          t("nurse-surgery-procedure.messages.sentToAdmissionUnit"),
          {
            variant: "success",
            autoHideDuration: 3000,
          }
        );
      } else if (statusId === 1750) {
        enqueueSnackbar(
          t("nurse-surgery-procedure.messages.acceptedToService"),
          {
            variant: "success",
            autoHideDuration: 3000,
          }
        );
      }

      // Update local state immediately after successful API call
      setSurgeryData((prevData) =>
        prevData.map((s) => {
          if (s.id !== surgery.id) {
            return s;
          }

          const statusLabelMap: Record<number, string> = {
            1650: t("nurse-surgery-procedure.statuses.called"),
            1655: t("nurse-surgery-procedure.statuses.preopCompleted"),
            1660: t("nurse-surgery-procedure.statuses.sentToOperatingRoom"),
            1715: t("nurse-surgery-procedure.statuses.sentToAdmissionUnit"),
            1750: t("nurse-surgery-procedure.statuses.acceptedToService"),
          };

          return {
            ...s,
            psid2: statusId,
            psname2: statusLabelMap[statusId] ?? "",
            iscomplatepreop: statusId === 1650 ? 0 : 1,
          };
        })
      );
    } catch (error) {
      console.error("❌ Error completing preop:", error);

      enqueueSnackbar(t("nurse-surgery-procedure.messages.preopError"), {
        variant: "error",
        autoHideDuration: 3000,
      });
    } finally {
      // Clear loading state
      setPreopLoading(null);
    }
  };

  return (
    <Box
      sx={{
        p: 2,
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        "@keyframes slideIn": {
          "0%": {
            opacity: 0,
            transform: "translateY(-10px)",
          },
          "100%": {
            opacity: 1,
            transform: "translateY(0)",
          },
        },
        "@keyframes slideOut": {
          "0%": {
            opacity: 1,
            transform: "translateY(0)",
          },
          "100%": {
            opacity: 0,
            transform: "translateY(-10px)",
          },
        },
      }}
    >
      {/* Top Control Bar */}
      <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
        {currentPatientId > 0 ? (
          <Paper sx={{ p: 0, flex: 1, borderRadius: 3 }}>
            <PatientDatas
              isCreateMedCard={true}
              isOpenMedCard={true}
              noMargin={true}
            />
          </Paper>
        ) : (
          <Paper sx={{ p: 1.5, flex: 1, borderRadius: 3 }}>
            <Grid container spacing={1} alignItems="center">
              {/* Date Selection */}
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <IconButton size="small" onClick={handlePreviousDay}>
                    <ArrowBackIcon />
                  </IconButton>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <ByzMuiDatePicker
                      value={selectedDate}
                      onChange={(newValue) => setSelectedDate(newValue)}
                      format="dd.MM.yyyy"
                      label={t("nurse-surgery-procedure.date")}
                      fullWidth
                    />
                  </LocalizationProvider>
                  <IconButton size="small" onClick={handleNextDay}>
                    <ArrowForwardIcon />
                  </IconButton>
                </Box>
              </Grid>

              {/* Filter Fields */}
              <Grid item xs={12} sm={6} md={2}>
                <BuildingFilter
                  onChange={handleBuildingChange}
                  value={building}
                  wrapInGrid={false}
                  sx={{ width: "100%" }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FloorList
                  handleChange={handleFloorChange}
                  hbId={building?.id || -1}
                  value={floor}
                  disabled={!building}
                  sx={{ width: "100%" }}
                  placeholder={!building ? t("nurse-surgery-procedure.select-building-first") || "Önce bina seçilmeli" : undefined}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2.5}>
                <DepartmentList
                  handleChange={handleDepartmentChange}
                  value={department}
                  sx={{ width: "100%" }}
                />
              </Grid>
            </Grid>
          </Paper>
        )}

        {/* Right Side - Action Buttons */}
        <Paper
          sx={{
            p: 1.5,
            minWidth: currentPatientId > 0 ? "120px" : "200px",
            display: "flex",
            alignItems: "center",
            borderRadius: 3,
          }}
        >
          {currentPatientId > 0 ? (
            // Patient-specific layout: Large Listele button, small stacked buttons
            <Box
              sx={{
                display: "flex",
                gap: 1,
                justifyContent: "flex-end",
                alignItems: "center",
                width: "100%",
              }}
            >
              <ByzMuiButton
                size="large"
                onClick={() => loadData()}
                sx={{
                  minHeight: "70px",
                  px: 3,
                  fontSize: "1.1rem",
                  fontWeight: "bold",
                  width: "120px",
                  flex: 1,
                }}
              >
                {t("nurse-surgery-procedure.list")}
              </ByzMuiButton>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                <ByzNursePrintButton2
                  variant="outlined"
                  size="small"
                  sx={{
                    minHeight: "70px",
                    px: 3,
                    width: "120px",
                  }}
                  iconSize="24px"
                  fontSize="1.1rem"
                  fontWeight={400}
                >
                  {t("nurse-surgery-procedure.print")}
                </ByzNursePrintButton2>
              </Box>
            </Box>
          ) : (
            // Filter layout: All buttons small and horizontal
            <Box
              sx={{
                display: "flex",
                gap: 1,
                justifyContent: "flex-end",
                alignItems: "center",
                width: "100%",
              }}
            >
              <ByzMuiButton
                variant2="outlined"
                size="small"
                onClick={() => loadData()}
                sx={{
                  width: "90px",
                }}
              >
                {t("nurse-surgery-procedure.list")}
              </ByzMuiButton>
              <ByzNursePrintButton2
                variant="outlined"
                size="small"
                sx={{
                  width: "90px",
                }}
              >
                {t("nurse-surgery-procedure.print")}
              </ByzNursePrintButton2>
            </Box>
          )}
        </Paper>
      </Box>

      {/* Surgery List Cards */}
      <Card sx={{ boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ p: 2, borderBottom: "1px solid #e0e0e0" }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: "bold", color: "#2c3e50" }}
            >
              {t("nurse-surgery-procedure.title")}
            </Typography>
          </Box>
          <Box sx={{ p: 2, minHeight: "200px" }}>
            {isLoading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: "200px",
                  flexDirection: "column",
                  gap: 2,
                }}
              >
                <CircularProgress size={40} />
                <Typography variant="body1" color="textSecondary">
                  {t("nurse-surgery-procedure.loading-surgery-data")}
                </Typography>
              </Box>
            ) : surgeryData.length > 0 ? (
              <Stack spacing={2}>
                {surgeryData.map((surgery, index) => {
                  const isExpanded = expandedCards.includes(surgery.id);
                  const isClosing = closingCards.includes(surgery.id);

                  return (
                    <Card
                      key={surgery.id}
                      sx={{
                        border: "1px solid #e0e0e0",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                        cursor: "pointer",
                        "&:hover": {
                          boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                          borderColor: "#1976d2",
                        },
                      }}
                      onClick={() =>
                        !isExpanded && handleToggleExpand(surgery.id)
                      }
                    >
                      <CardContent sx={{ p: 2 }}>
                        <Grid container spacing={2}>
                          {/* Left Side - Surgery Details */}
                          <Grid
                            item
                            xs={12}
                            md={isExpanded ? 10 : 11}
                            sx={{
                              transition: "all 0.2s ease-in-out",
                            }}
                          >
                            {/* Header Row */}
                            <Grid container spacing={2} sx={{ mb: 2 }}>
                              <Grid
                                item
                                xs={12}
                                sm={6}
                                md={isExpanded ? 3 : 2.4}
                              >
                                <Typography
                                  variant="body2"
                                  color="textSecondary"
                                  sx={{ fontSize: "0.75rem" }}
                                >
                                  {t("nurse-surgery-procedure.surgery-date")}
                                </Typography>
                                <Typography
                                  variant="body1"
                                  sx={{
                                    fontWeight: "medium",
                                    fontSize: "0.85rem",
                                  }}
                                >
                                  {new Date(surgery.otime).toLocaleDateString(
                                    "tr-TR"
                                  )}
                                </Typography>
                              </Grid>
                              <Grid
                                item
                                xs={12}
                                sm={6}
                                md={isExpanded ? 3 : 2.4}
                              >
                                <Typography
                                  variant="body2"
                                  color="textSecondary"
                                  sx={{ fontSize: "0.75rem" }}
                                >
                                  {t("nurse-surgery-procedure.planned-time")}
                                </Typography>
                                <Typography
                                  variant="body1"
                                  sx={{
                                    fontWeight: "medium",
                                    fontSize: "0.85rem",
                                  }}
                                >
                                  {new Date(surgery.otime).toLocaleTimeString(
                                    "tr-TR",
                                    { hour: "2-digit", minute: "2-digit" }
                                  )}
                                </Typography>
                              </Grid>
                              <Grid
                                item
                                xs={12}
                                sm={6}
                                md={isExpanded ? 3 : 2.4}
                              >
                                <Typography
                                  variant="body2"
                                  color="textSecondary"
                                  sx={{ fontSize: "0.75rem" }}
                                >
                                  {t("nurse-surgery-procedure.preop-complete")}
                                </Typography>
                                <Checkbox
                                  checked={surgery.iscomplatepreop === 1}
                                  size="small"
                                  disabled
                                />
                              </Grid>
                              <Grid
                                item
                                xs={12}
                                sm={6}
                                md={isExpanded ? 3 : 2.4}
                              >
                                <Typography
                                  variant="body2"
                                  color="textSecondary"
                                  sx={{ fontSize: "0.75rem" }}
                                >
                                  {t("nurse-surgery-procedure.status")}
                                </Typography>
                                <Chip
                                  label={surgery.psname2}
                                  color={
                                    surgery.psname2 === "Servise Kabul Edildi"
                                      ? "success"
                                      : "warning"
                                  }
                                  size="small"
                                  sx={{ fontSize: "0.7rem" }}
                                />
                              </Grid>
                              {!isExpanded && (
                                <Grid item xs={12} sm={6} md={2.4}>
                                  <Typography
                                    variant="body2"
                                    color="textSecondary"
                                    sx={{ fontSize: "0.75rem" }}
                                  >
                                    {t(
                                      "nurse-surgery-procedure.department-name"
                                    )}
                                  </Typography>
                                  <Typography
                                    variant="body1"
                                    sx={{
                                      fontWeight: "medium",
                                      fontSize: "0.85rem",
                                    }}
                                  >
                                    {surgery.deptname}
                                  </Typography>
                                </Grid>
                              )}
                            </Grid>

                            <Divider sx={{ my: 1 }} />

                            {/* Second Row */}
                            <Grid container spacing={2} sx={{ mb: 2 }}>
                              <Grid
                                item
                                xs={12}
                                sm={6}
                                md={isExpanded ? 3 : 2.4}
                              >
                                <Typography
                                  variant="body2"
                                  color="textSecondary"
                                  sx={{ fontSize: "0.75rem" }}
                                >
                                  {t("nurse-surgery-procedure.service-exit")}
                                </Typography>
                                <Typography
                                  variant="body1"
                                  sx={{
                                    fontWeight: "medium",
                                    fontSize: "0.85rem",
                                  }}
                                >
                                  {surgery.serdistime
                                    ? new Date(
                                        surgery.serdistime
                                      ).toLocaleTimeString("tr-TR", {
                                        hour: "2-digit",
                                        minute: "2-digit",
                                      })
                                    : "-"}
                                </Typography>
                              </Grid>
                              <Grid
                                item
                                xs={12}
                                sm={6}
                                md={isExpanded ? 3 : 2.4}
                              >
                                <Typography
                                  variant="body2"
                                  color="textSecondary"
                                  sx={{ fontSize: "0.75rem" }}
                                >
                                  {t(
                                    "nurse-surgery-procedure.operating-room-acceptance"
                                  )}
                                </Typography>
                                <Typography
                                  variant="body1"
                                  sx={{
                                    fontWeight: "medium",
                                    fontSize: "0.85rem",
                                  }}
                                >
                                  {surgery.acceptforop
                                    ? new Date(
                                        surgery.acceptforop
                                      ).toLocaleTimeString("tr-TR", {
                                        hour: "2-digit",
                                        minute: "2-digit",
                                      })
                                    : "-"}
                                </Typography>
                              </Grid>
                              <Grid
                                item
                                xs={12}
                                sm={6}
                                md={isExpanded ? 3 : 2.4}
                              >
                                <Typography
                                  variant="body2"
                                  color="textSecondary"
                                  sx={{ fontSize: "0.75rem" }}
                                >
                                  {t("nurse-surgery-procedure.file-number")}
                                </Typography>
                                <Typography
                                  variant="body1"
                                  sx={{
                                    fontWeight: "medium",
                                    fontSize: "0.85rem",
                                  }}
                                >
                                  {surgery.upn}
                                </Typography>
                              </Grid>
                              <Grid
                                item
                                xs={12}
                                sm={6}
                                md={isExpanded ? 3 : 2.4}
                              >
                                <Typography
                                  variant="body2"
                                  color="textSecondary"
                                  sx={{ fontSize: "0.75rem" }}
                                >
                                  {t("nurse-surgery-procedure.patient")}
                                </Typography>
                                <Typography
                                  variant="body1"
                                  sx={{
                                    fontWeight: "medium",
                                    fontSize: "0.85rem",
                                  }}
                                >
                                  {surgery.patientname} {surgery.patientsurname}
                                </Typography>
                              </Grid>
                              {!isExpanded && (
                                <Grid item xs={12} sm={6} md={2.4}>
                                  <Typography
                                    variant="body2"
                                    color="textSecondary"
                                    sx={{ fontSize: "0.75rem" }}
                                  >
                                    {t("nurse-surgery-procedure.doctor")}
                                  </Typography>
                                  <Typography
                                    variant="body1"
                                    sx={{
                                      fontWeight: "medium",
                                      fontSize: "0.85rem",
                                    }}
                                  >
                                    {surgery.ptdrname}
                                  </Typography>
                                </Grid>
                              )}
                            </Grid>

                            {/* Expandable Content */}
                            <Collapse
                              in={isExpanded}
                              timeout="auto"
                              unmountOnExit
                            >
                              <Divider sx={{ my: 2 }} />

                              {/* Third Row - Procedures and ICD */}
                              <Grid container spacing={2} sx={{ mb: 2 }}>
                                <Grid item xs={12} md={6}>
                                  <Typography
                                    variant="body2"
                                    color="textSecondary"
                                    sx={{ fontSize: "0.75rem", mb: 1 }}
                                  >
                                    {t(
                                      "nurse-surgery-procedure.procedure-name"
                                    )}
                                  </Typography>
                                  <Box
                                    sx={{ fontSize: "0.8rem", lineHeight: 1.3 }}
                                  >
                                    {surgery.iproperations
                                      .split("\n")
                                      .map((procedure: string, idx: number) => (
                                        <Typography
                                          key={idx}
                                          variant="body2"
                                          sx={{ mb: 0.5 }}
                                        >
                                          • {procedure}
                                        </Typography>
                                      ))}
                                  </Box>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                  <Typography
                                    variant="body2"
                                    color="textSecondary"
                                    sx={{ fontSize: "0.75rem", mb: 1 }}
                                  >
                                    {t("nurse-surgery-procedure.icd-codes")}
                                  </Typography>
                                  <Box
                                    sx={{ fontSize: "0.8rem", lineHeight: 1.3 }}
                                  >
                                    {surgery.diseasecodes
                                      .split("\n")
                                      .map((icd: string, idx: number) => (
                                        <Typography
                                          key={idx}
                                          variant="body2"
                                          sx={{ mb: 0.5 }}
                                        >
                                          • {icd}
                                        </Typography>
                                      ))}
                                  </Box>
                                </Grid>
                              </Grid>

                              <Divider sx={{ my: 1 }} />

                              {/* Fourth Row - Department and Doctor */}
                              <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                  <Typography
                                    variant="body2"
                                    color="textSecondary"
                                    sx={{ fontSize: "0.75rem" }}
                                  >
                                    {t(
                                      "nurse-surgery-procedure.department-name"
                                    )}
                                  </Typography>
                                  <Typography
                                    variant="body1"
                                    sx={{
                                      fontWeight: "medium",
                                      fontSize: "0.85rem",
                                    }}
                                  >
                                    {surgery.deptname}
                                  </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                  <Typography
                                    variant="body2"
                                    color="textSecondary"
                                    sx={{ fontSize: "0.75rem" }}
                                  >
                                    {t("nurse-surgery-procedure.doctor")}
                                  </Typography>
                                  <Typography
                                    variant="body1"
                                    sx={{
                                      fontWeight: "medium",
                                      fontSize: "0.85rem",
                                    }}
                                  >
                                    {surgery.ptdrname}
                                  </Typography>
                                </Grid>
                              </Grid>
                            </Collapse>
                          </Grid>

                          {/* Right Side - Expand Button or Action Buttons */}
                          <Grid
                            item
                            xs={12}
                            md={isExpanded ? 2 : 1}
                            sx={{
                              transition: "all 0.2s ease-in-out",
                            }}
                          >
                            {isExpanded ? (
                              <Stack
                                spacing={1.5}
                                sx={{
                                  minWidth: "120px",
                                  animation: isClosing
                                    ? "slideOut 0.2s ease-in-out"
                                    : "slideIn 0.2s ease-in-out",
                                }}
                              >
                                {/* Preop Preparations Button */}
                                <Grid container spacing={0.75} columns={10}>
                                  <Grid
                                    item
                                    xs={surgery.psid2 === 1655 ? 6 : 10}
                                  >
                                    <Button
                                      variant="contained"
                                      fullWidth
                                      size="small"
                                      color="primary"
                                      disabled={
                                        surgery.iscomplatepreop === 0 &&
                                        surgery.psid2 === 1650
                                          ? false
                                          : true
                                      }
                                      onClick={() =>
                                        handlePreopComplete(surgery, 1655)
                                      }
                                      sx={{
                                        fontWeight: "bold",
                                        fontSize: "0.8rem",
                                        py: 1.5,
                                        minHeight: "52px",
                                        whiteSpace: "normal",
                                        lineHeight: 1.2,
                                      }}
                                    >
                                      {preopLoading === surgery.id ? (
                                        <CircularProgress
                                          size={20}
                                          color="inherit"
                                        />
                                      ) : (
                                        <>
                                          {t(
                                            "nurse-surgery-procedure.preop-preparations-complete"
                                          )}
                                        </>
                                      )}
                                    </Button>
                                  </Grid>
                                  {surgery.psid2 === 1655 && (
                                    <Grid item xs={4}>
                                      <Button
                                        variant="contained"
                                        fullWidth
                                        size="small"
                                        onClick={() =>
                                          handlePreopComplete(surgery, 1715)
                                        }
                                        sx={{
                                          fontWeight: "bold",
                                          fontSize: "0.8rem",
                                          py: 1.5,
                                          minHeight: "52px",
                                          whiteSpace: "normal",
                                          lineHeight: 1.2,
                                          backgroundColor: "#ffffff",
                                          color: "#616161",
                                          border: "1px solid #bdbdbd",
                                          boxShadow: "none",
                                          "&:hover": {
                                            backgroundColor: "#f5f5f5",
                                            boxShadow: "none",
                                          },
                                        }}
                                      >
                                        Geri Al
                                      </Button>
                                    </Grid>
                                  )}
                                </Grid>

                                {/* Send to Operating Room Button */}
                                <Grid container spacing={0.75} columns={10}>
                                  <Grid
                                    item
                                    xs={surgery.psid2 === 1660 ? 6 : 10}
                                  >
                                    <Button
                                      variant="contained"
                                      fullWidth
                                      size="small"
                                      disabled={
                                        !(
                                          surgery.psid2 === 1655 ||
                                          (surgery.psid2 === 1650 &&
                                            surgery.iscomplatepreop === 1)
                                        )
                                      }
                                      onClick={() =>
                                        handlePreopComplete(surgery, 1660)
                                      }
                                      sx={{
                                        fontWeight: "bold",
                                        fontSize: "0.8rem",
                                        py: 1.5,
                                        minHeight: "52px",
                                        backgroundColor: "#424242",
                                        whiteSpace: "normal",
                                        lineHeight: 1.2,
                                        "&:hover": {
                                          backgroundColor: "#616161",
                                        },
                                      }}
                                    >
                                      {t(
                                        "nurse-surgery-procedure.send-to-operating-room"
                                      )}
                                    </Button>
                                  </Grid>
                                  {surgery.psid2 === 1660 && (
                                    <Grid item xs={4}>
                                      <Button
                                        variant="contained"
                                        fullWidth
                                        size="small"
                                        onClick={() =>
                                          handlePreopComplete(surgery, 1655)
                                        }
                                        sx={{
                                          fontWeight: "bold",
                                          fontSize: "0.8rem",
                                          py: 1.5,
                                          minHeight: "52px",
                                          whiteSpace: "normal",
                                          lineHeight: 1.2,
                                          backgroundColor: "#ffffff",
                                          color: "#616161",
                                          border: "1px solid #bdbdbd",
                                          boxShadow: "none",
                                          "&:hover": {
                                            backgroundColor: "#f5f5f5",
                                            boxShadow: "none",
                                          },
                                        }}
                                      >
                                        Geri Al
                                      </Button>
                                    </Grid>
                                  )}
                                </Grid>

                                {/* Accept to Service Button */}
                                <Button
                                  variant="contained"
                                  fullWidth
                                  size="small"
                                  color="success"
                                  disabled={
                                    surgery.psid2 === 1705 ? false : true
                                  }
                                  onClick={() =>
                                    handlePreopComplete(surgery, 1750)
                                  }
                                  sx={{
                                    fontWeight: "bold",
                                    fontSize: "0.8rem",
                                    py: 1.5,
                                    minHeight: "52px",
                                    whiteSpace: "normal",
                                    lineHeight: 1.2,
                                  }}
                                >
                                  {t(
                                    "nurse-surgery-procedure.accept-to-service"
                                  )}
                                </Button>

                                {/* Close Button */}
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "flex-end",
                                    alignItems: "center",
                                    height: "100%",
                                  }}
                                >
                                  <IconButton
                                    onClick={() =>
                                      handleToggleExpand(surgery.id)
                                    }
                                    sx={{
                                      border: "1px solid #e0e0e0",
                                      "&:hover": {
                                        backgroundColor: "#f5f5f5",
                                        borderColor: "#1976d2",
                                      },
                                    }}
                                  >
                                    <ExpandLessIcon />
                                  </IconButton>
                                </Box>
                              </Stack>
                            ) : (
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "flex-end",
                                  alignItems: "center",
                                  height: "100%",
                                }}
                              >
                                <IconButton
                                  onClick={() => handleToggleExpand(surgery.id)}
                                  sx={{
                                    border: "1px solid #e0e0e0",
                                    "&:hover": {
                                      backgroundColor: "#f5f5f5",
                                      borderColor: "#1976d2",
                                    },
                                  }}
                                >
                                  <ExpandMoreIcon />
                                </IconButton>
                              </Box>
                            )}
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  );
                })}
              </Stack>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "200px",
                  color: "text.secondary",
                }}
              >
                <Typography variant="h6" sx={{ textAlign: "center" }}>
                  {t("nurse-surgery-procedure.no-surgery-found")}
                </Typography>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
